
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Lock, Eye, EyeOff, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { analyzePasswordStrength, getSecureErrorMessage } from "@/utils/securityUtils";

// Schema for regular password change
const passwordSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required"),
  newPassword: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .refine((password) => {
      const analysis = analyzePasswordStrength(password);
      return analysis.isValid;
    }, {
      message: "Password must meet security requirements",
    }),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
}).refine((data) => data.currentPassword !== data.newPassword, {
  message: "New password must be different from current password",
  path: ["newPassword"],
});

// Schema for password reset (no current password required)
const passwordResetSchema = z.object({
  newPassword: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .refine((password) => {
      const analysis = analyzePasswordStrength(password);
      return analysis.isValid;
    }, {
      message: "Password must meet security requirements",
    }),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type PasswordFormData = z.infer<typeof passwordSchema>;
type PasswordResetFormData = z.infer<typeof passwordResetSchema>;

// Timeout helper to prevent indefinite hanging in restricted browser contexts
function withTimeout<T>(promise: Promise<T>, timeoutMs: number = 30000): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => 
      setTimeout(() => reject(new Error('Operation timed out. Please try again.')), timeoutMs)
    )
  ]);
}

interface ChangePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPasswordReset?: boolean;
  redirectTo?: string;
}

const ChangePasswordDialog = ({ 
  open, 
  onOpenChange, 
  isPasswordReset = false,
  redirectTo = "/seller/login"
}: ChangePasswordDialogProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const form = useForm<PasswordFormData | PasswordResetFormData>({
    resolver: zodResolver(isPasswordReset ? passwordResetSchema : passwordSchema),
    defaultValues: isPasswordReset 
      ? {
          newPassword: "",
          confirmPassword: "",
        }
      : {
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        },
  });

  const onSubmit = async (data: PasswordFormData | PasswordResetFormData) => {
    setIsLoading(true);
    
    try {
      // If not in password reset mode, verify current password first
      if (!isPasswordReset && 'currentPassword' in data) {
        const { data: userData } = await withTimeout(supabase.auth.getUser());
        const { error: verifyError } = await withTimeout(
          supabase.auth.signInWithPassword({
            email: userData.user?.email || "",
            password: data.currentPassword,
          })
        );

        if (verifyError) {
          toast({
            title: "Incorrect current password",
            description: "Please check your current password and try again.",
            variant: "destructive",
          });
          return;
        }
      }

      // Update password
      const { error: updateError } = await withTimeout(
        supabase.auth.updateUser({
          password: data.newPassword,
        })
      );

      if (updateError) {
        throw updateError;
      }

      // Clear form data for security
      form.reset();
      
      toast({
        title: "Password updated successfully",
        description: "For security, you will be signed out and need to log in again.",
      });

      // Close dialog
      onOpenChange(false);

      // Sign out user for security (both regular change and password reset)
      setTimeout(async () => {
        await signOut();
        // Navigate to correct login page (handles buyer vs seller)
        navigate(redirectTo, { replace: true });
      }, 2000);

    } catch (error: any) {
      console.error("Password change error:", error);
      toast({
        title: "Password change failed",
        description: getSecureErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            {isPasswordReset ? "Set New Password" : "Change Password"}
          </DialogTitle>
          <DialogDescription>
            {isPasswordReset 
              ? "Create a new secure password for your account."
              : "Update your account password. You'll be signed out after changing your password for security."
            }
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {!isPasswordReset && (
              <FormField
                control={form.control}
                name="currentPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Current Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          {...field}
                          type={showCurrentPassword ? "text" : "password"}
                          placeholder="Enter your current password"
                          className="pr-10"
                          disabled={isLoading}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          disabled={isLoading}
                        >
                          {showCurrentPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      placeholder="Enter your new password"
                      showStrengthMeter={true}
                      showGenerator={true}
                      disabled={isLoading}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm New Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      placeholder="Confirm your new password"
                      disabled={isLoading}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-godyspo-gold hover:bg-godyspo-gold/90 text-black"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isPasswordReset ? "Setting Password..." : "Changing Password..."}
                  </>
                ) : (
                  isPasswordReset ? "Set Password" : "Change Password"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordDialog;
